'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import './styles/@theme';
import { POPUPS, type PopupType } from './constants';
import { useStore } from '@/store/store';
import { ErrorBoundary } from '@/app/components/error-boundary/error-boundary';
import type { Region } from '@/types/constants';
import type { LogOutPopData } from '@/types/aggregation/common';
import type { PrismicObject } from '@/types/prismic/common';

const StateSelector = dynamic(
    () => import('@/app/components/popup/layouts/state-selector/state-selector').then((module) => module.StateSelector),
    { ssr: false }
);
const LoyaltyPopup = dynamic(
    () => import('@/app/components/popup/layouts/loyalty-popup/loyalty-popup').then((module) => module.LoyaltyPopup),
    { ssr: false }
);
const ExclusivePopup = dynamic(
    () =>
        import('@/app/components/popup/layouts/exclusive-popup/exclusive-popup').then(
            (module) => module.ExclusivePopup
        ),
    { ssr: false }
);
const LogoutPop = dynamic(() => import('@/app/components/popup/layouts/logout').then((module) => module.LogoutPop), {
    ssr: false
});
const GameInfoModal = dynamic(
    () => import('@/app/components/game-info-modal/game-info-modal').then((module) => module.GameInfoModal),
    { ssr: false }
);
const VolatilityInfoPopup = dynamic(
    () =>
        import('@/app/components/popup/layouts/volatility-info-popup/volatility-info-popup').then(
            (module) => module.VolatilityInfoPopup
        ),
    { ssr: false }
);
const SWJOptInPopUp = dynamic(
    () => import('@/app/components/popup/layouts/swj-opt-in').then((module) => module.SWJOptInPopUp),
    { ssr: false }
);
const SWJErrorPopUp = dynamic(
    () => import('@/app/components/popup/layouts/swj-error').then((module) => module.SWJErrorPopUp),
    { ssr: false }
);
const DYBInterstitialPopup = dynamic(
    () => import('@/app/components/popup/layouts/dyb-interstitial').then((module) => module.DYBInterstitialPopup),
    { ssr: false }
);
const BiggestWinModal = dynamic(
    () => import('../biggest-win-modal/biggest-win-modal').then((module) => module.BiggestWinModal),
    { ssr: false }
);
const RewardMachineWelcomeModal = dynamic(
    () =>
        import('../reward-machine/reward-machine-welcome-modal/reward-machine-welcome-modal').then(
            (module) => module.RewardMachineWelcomeModal
        ),
    { ssr: false }
);

type PopupManagerClientProps = {
    regions: Region[];
    logoutPopup?: PrismicObject<'logout_popup', LogOutPopData>;
};

type PopupParams = Record<string, unknown>;

/**
 * Defines which popup types can overlay other popups.
 * Key: popup type that can appear on top
 * Value: array of popup types it can overlay
 */
const OVERLAY_CONFIG: Partial<Record<PopupType, PopupType[]>> = {
    [POPUPS.SWJ_ERROR]: [POPUPS.SWJ_OPT_IN],
    [POPUPS.VOLATILITY_INFO]: [POPUPS.GAME_INFO]
};

const renderPopupContent = (
    type: PopupType,
    params: PopupParams,
    regions: Region[],
    logoutPopup?: PrismicObject<'logout_popup', LogOutPopData>
): React.ReactNode => {
    switch (type) {
        case POPUPS.STATE_SELECTOR:
            return <StateSelector regions={regions} />;
        case POPUPS.MOMENTUM_LOYALTY:
            return <LoyaltyPopup />;
        case POPUPS.LOGOUT:
            return <LogoutPop logoutPopup={logoutPopup} />;
        case POPUPS.EXCLUSIVE_ZONE:
            return (
                <ErrorBoundary name="exclusive-popup">
                    <ExclusivePopup />
                </ErrorBoundary>
            );
        case POPUPS.GAME_INFO:
            return <GameInfoModal />;
        case POPUPS.VOLATILITY_INFO:
            return <VolatilityInfoPopup />;
        case POPUPS.SWJ_OPT_IN:
            return <SWJOptInPopUp params={params} />;
        case POPUPS.SWJ_ERROR:
            return <SWJErrorPopUp params={params} />;
        case POPUPS.DYB_INTERSTITIAL:
            return <DYBInterstitialPopup params={params} />;
        case POPUPS.BIGGEST_WIN:
            return <BiggestWinModal />;
        case POPUPS.REWARD_MACHINE_WELCOME_MODAL:
            return <RewardMachineWelcomeModal params={params} />;
        default:
            return null;
    }
};

/**
 * Filters the queue to only include popups that should be visible.
 * By default, only the first popup is shown.
 * If the first popup can overlay specific types, those are also included.
 */
const getVisiblePopups = (queue: { id: string; type: PopupType; params: PopupParams }[]) => {
    if (queue.length === 0) return [];

    const firstPopup = queue[0];
    const overlayableTypes = OVERLAY_CONFIG[firstPopup.type as PopupType];

    if (!overlayableTypes) {
        return [firstPopup];
    }

    return queue.filter((item, index) => index === 0 || overlayableTypes.includes(item.type as PopupType));
};

const BASE_Z_INDEX = 6;

export const PopupClient: React.FC<PopupManagerClientProps> = ({ regions, logoutPopup }) => {
    const queue = useStore((state) => state.popup.queue);

    if (queue.length === 0) return null;

    const visiblePopups = getVisiblePopups(queue);
    const lastIndex = visiblePopups.length - 1;

    return (
        <>
            {visiblePopups.map((item, index) => {
                const content = renderPopupContent(item.type as PopupType, item.params, regions, logoutPopup);
                if (!content) return null;

                const zIndex = BASE_Z_INDEX + lastIndex - index;

                return (
                    <div key={item.id} className="popup" style={{ zIndex }}>
                        <ErrorBoundary name="popup">{content}</ErrorBoundary>
                    </div>
                );
            })}
        </>
    );
};
